import {
    BackgroundCheckStatus,
    ScreeningRequestStatus,
    ScreeningResult,
} from '../models/Outreach/types'

export function getScreeningRequestStatusDisplay(
    status: ScreeningRequestStatus | string | null | undefined,
): { label: string; tone: 'neutral' | 'good' | 'warn' | 'bad' } {
    switch (status) {
        case 'INVITED':
            return { label: 'Invited', tone: 'warn' }
        case 'PROCESSING':
            return { label: 'Processing', tone: 'warn' }
        case 'COMPLETE':
            return { label: 'Complete', tone: 'good' }
        case 'FAILED':
            return { label: 'Failed', tone: 'bad' }
        case 'EXPIRED':
            return { label: 'Expired', tone: 'bad' }
        case 'PENDING':
            return { label: 'Pending', tone: 'neutral' }
        default:
            return {
                label: status ? String(status) : 'Not started',
                tone: 'neutral',
            }
    }
}

export function getScreeningResultDisplay(
    result: ScreeningResult | string | null | undefined,
): { label: string; tone: 'neutral' | 'good' | 'warn' | 'bad' } {
    switch (result) {
        case 'approved':
            return { label: 'Approved', tone: 'good' }
        case 'approved_prior_resident':
            return { label: 'Approved — prior resident', tone: 'good' }
        case 'conditional':
            return { label: 'Consider', tone: 'warn' }
        case 'declined':
            return { label: 'Declined', tone: 'bad' }
        case 'pending':
            return { label: 'Pending', tone: 'neutral' }
        default:
            return { label: result ? String(result) : '—', tone: 'neutral' }
    }
}

export function getBackgroundCheckDisplay(
    status: BackgroundCheckStatus | string | null | undefined,
): { label: string; tone: 'neutral' | 'good' | 'warn' | 'bad' } {
    switch (status) {
        case 'clear':
            return { label: 'Clear', tone: 'good' }
        case 'consider':
            return { label: 'Consider', tone: 'warn' }
        case 'pending':
            return { label: 'Pending', tone: 'neutral' }
        default:
            return { label: status ? String(status) : '—', tone: 'neutral' }
    }
}

export function canStartScreening(params: {
    pipelineStage?: string | null
    screeningStatus?: ScreeningRequestStatus | string | null
    screeningResult?: ScreeningResult | string | null
}): boolean {
    if (params.pipelineStage !== 'screening') return false
    if (
        params.screeningResult === 'approved' ||
        params.screeningResult === 'approved_prior_resident'
    ) {
        return false
    }
    const status = params.screeningStatus
    if (!status) return true
    // New invite after a non-approved complete, or retry when the prior attempt failed.
    return status === 'COMPLETE' || status === 'FAILED'
}

export function canResendScreeningInvitation(params: {
    pipelineStage?: string | null
    screeningStatus?: ScreeningRequestStatus | string | null
    invitationUrl?: string | null
    candidateId?: string | null
}): boolean {
    if (params.pipelineStage !== 'screening') return false
    const status = params.screeningStatus
    if (status === 'INVITED' && params.invitationUrl) return true
    if ((status === 'EXPIRED' || status === 'FAILED') && params.candidateId) {
        return true
    }
    return false
}

export function canSendScreeningLink(params: {
    pipelineStage?: string | null
    applicationStatus?: string | null
    pendingApplicationReview?: boolean
    screeningStatus?: ScreeningRequestStatus | string | null
}): boolean {
    const status = params.screeningStatus
    if (
        status === 'INVITED' ||
        status === 'PROCESSING' ||
        status === 'PENDING'
    ) {
        return false
    }
    if (
        params.pipelineStage === 'app_signed_pending_review' ||
        params.pendingApplicationReview
    ) {
        return true
    }
    if (
        params.pipelineStage === 'app_sent' &&
        params.applicationStatus === 'submitted'
    ) {
        return true
    }
    return false
}

/** Lease offer from Screening / package from Leases: only after an approved (Clear) outcome. */
export function canSendLeasePackage(params: {
    screeningResult?: ScreeningResult | string | null
}): boolean {
    return (
        params.screeningResult === 'approved' ||
        params.screeningResult === 'approved_prior_resident'
    )
}
